import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import { type z } from 'zod';
import type { SchoolSearchRes } from '@/api/gen/_model/schoolSearchRes';
import { DefaultDialog } from '@/ui_v3';
import { type StudentSchema, type TeacherSchema } from '../../schema';
import { SchoolSearchModalBody } from './SchoolSearchModalBody';
import { SchoolSearchModalFooter } from './SchoolSearchModalFooter';
export type SchoolFormData = z.infer<typeof StudentSchema | typeof TeacherSchema>;
interface SchoolSearchModalProps {
  onCancel: () => void;
  onConfirm: (data: {
    schoolName: string;
    schoolType: string;
    schoolGrade: string;
    verifiedSchool: {
      standardCode: string;
    };
    schoolTypeCode: string;
    schoolGradeCode: string;
  }) => void;
  countryCode: string;
  /** 학년 선택지 분기용(교사만 전학년 노출). 미지정 시 전학년 미노출. */
  userType?: string;
}
export const SchoolSearchModal = ({
  onCancel,
  onConfirm,
  countryCode,
  userType
}: SchoolSearchModalProps) => {
  const {
    t
  } = useTranslation(['common']);

  // 모달 내부 전용 임시 폼
  const tempFormControl = useForm<SchoolFormData>({
    defaultValues: {
      schoolName: '',
      schoolType: 'PRIMARY',
      schoolGrade: '',
      verifiedSchool: {
        standardCode: ''
      },
      countryCode: countryCode,
      // countryCode 추가
      // 임시 폼이라 바깥 폼의 값이 안 넘어온다. 학년 셀렉(SignSelectSchoolGrade)이
      // userType 으로 전학년 노출을 분기하므로 countryCode 와 같이 명시 주입한다.
      userType: userType
    }
  });
  const [selectedSchool, setSelectedSchool] = useState<SchoolSearchRes | null>(null);
  const [isManualMode, setIsManualMode] = useState(false);
  const handleConfirm = () => {
    // 확인 버튼 클릭 시에만 데이터 전달
    const formData = tempFormControl.getValues();
    onConfirm({
      schoolName: formData.schoolName || '',
      schoolType: formData.schoolType || '',
      schoolGrade: formData.schoolGrade || '',
      verifiedSchool: formData.verifiedSchool || {
        standardCode: ''
      },
      schoolTypeCode: formData.schoolTypeCode || '',
      schoolGradeCode: formData.schoolGradeCode || ''
    });
  };
  return <FormProvider {...tempFormControl} data-sentry-element="FormProvider" data-sentry-component="SchoolSearchModal" data-sentry-source-file="SchoolSearchModal.tsx">
      <DefaultDialog onClose={onCancel} size="md" header={{
      title: t('signup.school.searchButton'),
      hasCloseButton: true
    }} body={<SchoolSearchModalBody selectedSchool={selectedSchool} setSelectedSchool={setSelectedSchool} isManualMode={isManualMode} setIsManualMode={setIsManualMode} />} footer={selectedSchool || isManualMode ? <SchoolSearchModalFooter isManualMode={isManualMode} onCancel={onCancel} onConfirm={handleConfirm} /> : null} shouldCloseOnDimClick={false} data-sentry-element="DefaultDialog" data-sentry-source-file="SchoolSearchModal.tsx" />
    </FormProvider>;
};
export const openSchoolSearchModal = ({
  onConfirm,
  onClose,
  countryCode,
  userType
}: {
  onConfirm: (data: {
    schoolName: string;
    schoolType: string;
    schoolGrade: string;
    verifiedSchool: {
      standardCode: string;
    };
    schoolTypeCode: string;
    schoolGradeCode: string;
  }) => void;
  /** 확인/취소로 모달이 닫힐 때 호출된다(선택). 중첩 모달 포커스 처리 등에 사용. */
  onClose?: () => void;
  countryCode: string;
  /** 학년 선택지 분기용(교사만 전학년 노출). 미지정 시 전학년 미노출. */
  userType?: string;
}) => {
  return overlay.open(({
    unmount
  }) => <SchoolSearchModal onCancel={() => {
    onClose?.();
    unmount();
  }} onConfirm={data => {
    onConfirm(data);
    onClose?.();
    unmount();
  }} countryCode={countryCode} userType={userType} />);
};